import { createSlice } from '@reduxjs/toolkit'
import {
  getMttLobbyThunk,
  getMttOverviewThunk,
  registerMttThunk,
  unregisterMttThunk
} from 'redux-thunk/thunk/mtt.thunk'

const REGISTERED_IDS_KEY = 'mtt_registered_tournament_ids'

const loadRegisteredIds = () => {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(REGISTERED_IDS_KEY) || '[]')
    return Array.isArray(parsed) ? parsed.map(String) : []
  } catch {
    return []
  }
}

const persistRegisteredIds = (ids) => {
  try {
    sessionStorage.setItem(REGISTERED_IDS_KEY, JSON.stringify(ids.map(String)))
  } catch {
    // Storage may be unavailable in private browsing.
  }
}

const initialState = {
  tournaments: [],
  registeredTournamentIds: loadRegisteredIds(),
  overview: null,
  blindLevel: null,
  clock: null,
  playerMoved: null,
  loading: false,
  actionLoading: false,
  error: null
}

const mttSlice = createSlice({
  name: 'mtt',
  initialState,
  reducers: {
    applyMttLobbyUpdate: (state, action) => {
      const data = action.payload || {}
      const tournamentId = String(data.tournamentId || data.id || '')
      if (!tournamentId) return

      const index = state.tournaments.findIndex((row) => String(row.id) === tournamentId)
      if (index === -1) {
        state.tournaments.push({ id: tournamentId, ...data })
        return
      }

      state.tournaments[index] = { ...state.tournaments[index], ...data, id: state.tournaments[index].id }
      if (['COMPLETED', 'CANCELLED', 'CLOSED'].includes(data.status)) {
        if (state.tournaments[index].myRegistration) {
          state.tournaments[index].myRegistration.historyOnly = true
        }
        state.registeredTournamentIds = state.registeredTournamentIds.filter((id) => id !== tournamentId)
        persistRegisteredIds(state.registeredTournamentIds)
      }
    },
    setMttOverview: (state, action) => {
      state.overview = { ...(state.overview || {}), ...(action.payload || {}) }
    },
    setMttClock: (state, action) => {
      const data = action.payload || {}
      state.clock = data
      state.blindLevel = data
      state.overview = { ...(state.overview || {}), ...data }
    },
    setMttPlayerMoved: (state, action) => {
      const data = action.payload || null
      state.playerMoved = data
      if (!data?.isMe || !data.tournamentId || !data.toTournamentTableId) return
      const tournamentId = String(data.tournamentId)
      const dest = data.toTournamentTableId
      const index = state.tournaments.findIndex((row) => String(row.id) === tournamentId)
      if (index !== -1 && state.tournaments[index].myRegistration) {
        state.tournaments[index].myRegistration = {
          ...state.tournaments[index].myRegistration,
          tournamentTableId: dest,
          seatNumber: data.toSeatNumber ?? state.tournaments[index].myRegistration.seatNumber,
          status: 'SEATED',
          waitingSeat: false
        }
      }
      // Keep overview leaderboard in sync so URL reconcile does not bounce back.
      if (state.overview?.leaderboard && data.userId != null) {
        state.overview = {
          ...state.overview,
          leaderboard: state.overview.leaderboard.map((row) => {
            if (String(row.userId) !== String(data.userId)) return row
            return {
              ...row,
              tournamentTableId: dest,
              seatNumber: data.toSeatNumber ?? row.seatNumber,
              waitingSeat: false,
              status: row.status === 'WAITING_SEAT' ? 'SEATED' : row.status
            }
          })
        }
      }
    },
    clearMttGameState: (state) => {
      state.overview = null
      state.blindLevel = null
      state.clock = null
      state.playerMoved = null
    },
    markRegistered: (state, action) => {
      const id = String(action.payload)
      if (!state.registeredTournamentIds.includes(id)) {
        state.registeredTournamentIds.push(id)
        persistRegisteredIds(state.registeredTournamentIds)
      }
    },
    markUnregistered: (state, action) => {
      const id = String(action.payload)
      state.registeredTournamentIds = state.registeredTournamentIds.filter((row) => row !== id)
      persistRegisteredIds(state.registeredTournamentIds)
    },
    // Bust-out: drop the seat locally so the lobby's auto-rejoin effect does not
    // send the player back to a table they were just eliminated from.
    markMyRegistrationEliminated: (state, action) => {
      const { tournamentId, finishPlace, prizeAmount } = action.payload || {}
      const id = String(tournamentId || '')
      if (!id) return
      const row = state.tournaments.find((item) => String(item.id) === id)
      if (row?.myRegistration) {
        row.myRegistration = {
          ...row.myRegistration,
          status: 'ELIMINATED',
          finishPlace: finishPlace ?? row.myRegistration.finishPlace,
          prizeAmount: prizeAmount ?? row.myRegistration.prizeAmount,
          canForfeit: false,
          canRejoin: false,
          withdrawn: true
        }
      }
      state.registeredTournamentIds = state.registeredTournamentIds.filter((item) => item !== id)
      persistRegisteredIds(state.registeredTournamentIds)
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getMttLobbyThunk.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getMttLobbyThunk.fulfilled, (state, action) => {
        state.loading = false
        state.tournaments = Array.isArray(action.payload?.tournaments)
          ? action.payload.tournaments
          : []
        state.registeredTournamentIds = state.tournaments
          .filter((row) => row.myRegistration && !row.myRegistration.historyOnly &&
            ['REGISTERED', 'SEATED'].includes(row.myRegistration.status))
          .map((row) => String(row.id))
        persistRegisteredIds(state.registeredTournamentIds)
      })
      .addCase(getMttLobbyThunk.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Failed to fetch MTT lobby'
      })
      .addCase(registerMttThunk.pending, (state) => {
        state.actionLoading = true
        state.error = null
      })
      .addCase(registerMttThunk.fulfilled, (state, action) => {
        state.actionLoading = false
        const id = String(action.payload?.tournamentId || '')
        if (id && !state.registeredTournamentIds.includes(id)) {
          state.registeredTournamentIds.push(id)
          persistRegisteredIds(state.registeredTournamentIds)
        }
        const row = state.tournaments.find((item) => String(item.id) === id)
        if (row) {
          if (action.payload?.entriesCount != null) row.entriesCount = action.payload.entriesCount
          if (action.payload?.prizePool != null) row.prizePool = action.payload.prizePool
          row.myRegistration = {
            ...(row.myRegistration || {}),
            id: action.payload?.registrationId,
            status: action.payload?.status || 'REGISTERED',
            canForfeit: true,
            historyOnly: false
          }
        }
      })
      .addCase(registerMttThunk.rejected, (state, action) => {
        state.actionLoading = false
        state.error = action.payload || 'Failed to register for MTT'
      })
      .addCase(unregisterMttThunk.pending, (state) => {
        state.actionLoading = true
        state.error = null
      })
      .addCase(unregisterMttThunk.fulfilled, (state, action) => {
        state.actionLoading = false
        const id = String(action.payload?.tournamentId || '')
        state.registeredTournamentIds = state.registeredTournamentIds.filter((row) => row !== id)
        persistRegisteredIds(state.registeredTournamentIds)
        const tournament = state.tournaments.find((row) => String(row.id) === id)
        if (tournament?.myRegistration) {
          tournament.myRegistration.status = action.payload?.status || 'FORFEITED'
          tournament.myRegistration.canForfeit = false
        }
      })
      .addCase(unregisterMttThunk.rejected, (state, action) => {
        state.actionLoading = false
        state.error = action.payload || 'Failed to forfeit MTT entry'
      })
      .addCase(getMttOverviewThunk.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getMttOverviewThunk.fulfilled, (state, action) => {
        state.loading = false
        state.overview = action.payload
          ? { ...(state.overview || {}), ...action.payload }
          : null
        if (action.payload) {
          state.clock = {
            tournamentId: action.payload.tournamentId,
            currentLevel: action.payload.currentLevel,
            smallBlind: action.payload.smallBlind,
            bigBlind: action.payload.bigBlind,
            ante: action.payload.ante,
            levelStartedAt: action.payload.levelStartedAt,
            durationSeconds: action.payload.durationSeconds,
            nextLevel: action.payload.nextLevel
          }
          state.blindLevel = state.clock
        }
      })
      .addCase(getMttOverviewThunk.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Failed to fetch MTT overview'
      })
  }
})

export const {
  applyMttLobbyUpdate,
  setMttOverview,
  setMttClock,
  setMttPlayerMoved,
  clearMttGameState,
  markRegistered,
  markUnregistered,
  markMyRegistrationEliminated
} = mttSlice.actions

export default mttSlice.reducer
